import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../utils/apiClient';

/**
 * @param {Object} [options]
 * @param {number|null} [options.limit=null] - размер страницы (по умолчанию решает сервер)
 * @param {boolean} [options.enabled=true] - если false, запрос не выполняется
 *   (например, пока пользователь ничего не ввёл в поиске)
 */
export function usePosts({ idUser = null, side = true, category = 'posts', feed = 'feed', sort = null, q = null, limit = null, enabled = true } = {}) {
    const [posts, setPosts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const cursorRef = useRef(null);
    const loadingMoreRef = useRef(false);

    const buildUrl = useCallback((cursor) => {
        const url = new URL('/api/posts', process.env.REACT_APP_API_URL);
        if (idUser) url.searchParams.append('idUser', idUser);
        url.searchParams.append('side', side);
        url.searchParams.append('category', category);
        url.searchParams.append('feed', feed);
        if (sort) url.searchParams.append('sort', sort);
        if (q) url.searchParams.append('q', q);
        if (limit) url.searchParams.append('limit', limit);
        if (cursor) url.searchParams.append('cursor', cursor);
        return url.toString();
    }, [idUser, side, category, feed, sort, q, limit]);

    const authHeaders = () => {
        const accessToken = localStorage.getItem('accessToken') ?? null;
        return {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        };
    };

    // Первичная загрузка + сброс при смене параметров
    useEffect(() => {
        let isMounted = true;

        // Запрос отключён — ничего не грузим и очищаем прошлый результат
        if (!enabled) {
            setPosts(null);
            setError(null);
            setHasMore(false);
            setLoading(false);
            cursorRef.current = null;
            return;
        }

        const fetchInitial = async () => {
            try {
                setLoading(true);
                setError(null);
                cursorRef.current = null;

                const response = await apiFetch(buildUrl(null), {
                    method: 'GET',
                    credentials: 'include',
                    headers: authHeaders(),
                });

                const data = await response.json();
                if (!response.ok) throw new Error('Ошибка сервера');

                if (isMounted) {
                    if (data.error) throw new Error(data.error);
                    setPosts(data.posts ?? []);
                    cursorRef.current = data.nextCursor ?? null;
                    setHasMore(!!data.nextCursor);
                }
            } catch (err) {
                if (isMounted) setError(err.message);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchInitial();

        return () => {
            isMounted = false;
        };
    }, [buildUrl, enabled]);

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || !cursorRef.current || !enabled) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);

        try {
            const response = await apiFetch(buildUrl(cursorRef.current), {
                method: 'GET',
                credentials: 'include',
                headers: authHeaders(),
            });

            const data = await response.json();
            if (!response.ok || data.error) throw new Error(data.error ?? 'Ошибка сервера');

            setPosts(prev => [...(prev ?? []), ...(data.posts ?? [])]);
            cursorRef.current = data.nextCursor ?? null;
            setHasMore(!!data.nextCursor);
        } catch (err) {
            // Не роняем уже загруженную ленту — просто прекращаем подгрузку
            setHasMore(false);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [buildUrl, enabled]);

    return { posts, loading, error, hasMore, loadingMore, loadMore };
}
